import { useState, type FormEvent } from 'react';
import { nip19 } from 'nostr-tools';
import { ScanSearch, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface TrustSearchProps {
  defaultValue?: string;
  autoFocus?: boolean;
  loading?: boolean;
  onAnalyze: (pubkey: string, npub: string) => void;
  className?: string;
}

/** Parse an npub / nprofile / raw hex pubkey into { pubkey, npub }. */
function parsePubkeyInput(raw: string): { pubkey: string; npub: string } | null {
  const value = raw.trim();
  if (!value) return null;

  if (/^[0-9a-f]{64}$/i.test(value)) {
    const pubkey = value.toLowerCase();
    return { pubkey, npub: nip19.npubEncode(pubkey) };
  }

  try {
    const decoded = nip19.decode(value.replace(/^nostr:/, ''));
    if (decoded.type === 'npub') {
      return { pubkey: decoded.data, npub: value };
    }
    if (decoded.type === 'nprofile') {
      return { pubkey: decoded.data.pubkey, npub: nip19.npubEncode(decoded.data.pubkey) };
    }
  } catch {
    return null;
  }

  return null;
}

export function TrustSearch({
  defaultValue = '',
  autoFocus,
  loading,
  onAnalyze,
  className,
}: TrustSearchProps) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parsePubkeyInput(value);
    if (!parsed) {
      setError('Enter a valid npub, nprofile, or 64-char hex pubkey.');
      return;
    }
    setError(null);
    onAnalyze(parsed.pubkey, parsed.npub);
  };

  return (
    <form onSubmit={handleSubmit} className={cn('w-full', className)}>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            placeholder="npub1… nprofile1… or hex pubkey"
            autoFocus={autoFocus}
            spellCheck={false}
            autoComplete="off"
            aria-label="Nostr public key"
            aria-invalid={!!error}
            className="h-12 bg-card/80 border-primary/40 font-mono text-sm pr-4 focus-visible:border-primary placeholder:text-muted-foreground/60"
          />
        </div>
        <Button type="submit" size="lg" disabled={loading} className="h-12 px-6">
          {loading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ScanSearch className="size-4" />
          )}
          Analyze
        </Button>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {error}
        </p>
      )}
    </form>
  );
}
